import { useMemo } from 'react';

// Third-party
import { useMediaQuery, useTheme } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid2';
import TextField from '@mui/material/TextField';
import FormControlLabel from '@mui/material/FormControlLabel';
import { useTranslation } from 'react-i18next';
import Icon from '@mdi/react';
import { mdiAsterisk } from '@mdi/js';
import moment from 'moment';
import LoadingButton from '@mui/lab/LoadingButton';
import { useSnackbar } from 'notistack';
import Divider from '@mui/material/Divider';

// Components
import {
  BaseDrawerTitle,
  BaseDrawer,
  BaseLabel,
  BaseCheckbox,
  BaseCombobox,
} from '@mgm/common/components/base';
import {
  CustomTextField,
} from '@mgm/common/components/form';
import { BASE_COLORS, getTimezone } from '@mgm/common/utils';
import {
  AvatarSelect,
} from '../../../../components/base';
import { InputDatePicker } from '../../../../components/pickers';
import { CreateEditTasks } from './CreateEditTasks';

// Hooks
import { useOptions } from '../../../../hooks/useOptions';
import { useAppDispatch, useAppSelector } from '../../../../hooks/useAppStore';
import { useFormRules } from '../../../../hooks/useForm';
import { useAddTasklistAPI, useEditTasklistAPI } from '../../Shared/hooks/api';
import { useEditTasklist } from '../hooks';

// Redux-toolkit
import { getMembers } from '../../../../store/team/selectors';
import { getSelectedTasklistForAction } from '../../../../store/tasklists/selectors';
import { getIsOpenPagesNav } from '../../../../store/common/selectors';
import { commonActions } from '../../../../store/common/slice';
import { tasklistsActions } from '../../../../store/tasklists/slice';

// Utils

// Types
import { TLocation, TTasklistTask, TTasklist } from '../../../../types';
import { useCommon } from '../../../../hooks/useCommon';

const CreateEditTasklist = () => {
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation(['inputs', 'common', 'tasklists']);
  const { roleOptions } = useOptions();
  const { locationsByPermissions } = useCommon();

  const {
    mode,
    formInputs,
    setFormInputs,
    isEdited,
    getFormInputs,
  } = useEditTasklist();
  const { taskValidation } = useFormRules();
  const dispatch = useAppDispatch();

  const members = useAppSelector(getMembers);
  const setSelectedTasklistForAction = useAppSelector(getSelectedTasklistForAction);
  const isOpenPagesNav = useAppSelector(getIsOpenPagesNav);
  const { mutateAsync: editMutate, isLoading: isEditLoading } = useEditTasklistAPI();
  const { mutateAsync: addMutate, isLoading: isAddLoading } = useAddTasklistAPI();

  const methods = useForm({
    mode: 'all',
    values: {
      name: formInputs.name || '',
      tasklistPicker: formInputs.date,
    },
  });

  const getMembersByPermission = useMemo(() => {
    if (formInputs.visibility?.length) {
      const formattedRoles = formInputs.visibility?.map((item) => item?.id);

      return members.filter((member) => formattedRoles.includes(member.permissionLevel?.toLowerCase()));
    }
    return [];
  }, [formInputs.visibility, members]);

  const dataByMode = useMemo(() => {
    const isCreateMode = mode === 'create';
    return {
      title: isCreateMode ? t('addTasklist', { ns: 'tasklists' }) : t('editTasklist', { ns: 'tasklists' }),
      mainBtnText: isCreateMode ? t('createTasklist', { ns: 'tasklists' }) : t('editTasklist', { ns: 'tasklists' }),
      isCreateMode,
    };
  }, [mode]);

  const handleCloseNav = () => {
    dispatch(commonActions.setIsOpenPagesNav(false));

    setTimeout(() => {
      dispatch(tasklistsActions.setSelectedTasklistForAction({} as TTasklist));
    }, 300);
  };

  const addOrEditTasklist = async (formValues: { name: string }) => {
    if (dataByMode.isCreateMode) {
      const data = {
        ...formInputs,
        name: formValues.name,
      };

      await addMutate(data);
    } else {
      const rsp = await editMutate({ tasklistData: formInputs, tasklistId: setSelectedTasklistForAction.id });
      if (rsp.success) {
        enqueueSnackbar(`${t('tasklistWasUpdated', { ns: 'tasklists' })}`, { variant: 'success' });
      }
    }
    handleCloseNav();
  };

  const handleFormChange = (key: string, value: unknown) => {
    setFormInputs((prevState) => ({
      ...prevState, [key]: value,
    }));
  };

  const addTask = (taskName: string) => {
    setFormInputs((prevState) => ({
      ...prevState,
      tasks: [...prevState.tasks, {
        taskName,
        index: prevState.tasks.length,
      }] as Array<TTasklistTask>,
    }));
  };

  const deleteTask = (taskIndex: number) => {
    setFormInputs((prevState) => ({
      ...prevState,
      tasks: prevState.tasks.filter((task) => task.index !== taskIndex),
    }));
  };

  const onCloseDrawer = () => {
    dispatch(commonActions.setToggleDrawer(false));
  };

  return (
    <BaseDrawer
      drawerProps={{
        anchor: 'right',
        width: isMobile ? 'auto' : 600,
        variant: 'persistent',
      }}
      onCloseDrawer={onCloseDrawer}
      open={isOpenPagesNav}
    >
      <BaseDrawerTitle handleClose={handleCloseNav} title={dataByMode.title} />
      <Divider sx={{ borderStyle: 'dashed' }} />
      <FormProvider {...methods}>
        <Box
          paddingRight={isMobile ? 2 : 5}
          paddingLeft={isMobile ? 2 : 5}
          component="form"
          onSubmit={(e) => {
            e.preventDefault();
          }}
        >
          <Grid paddingTop={2} container spacing={4}>
            <Grid size={{
              xs: 12, sm: 6, md: 6, lg: 6,
            }}
            >
              <CustomTextField
                onChange={(name) => {
                  handleFormChange('name', name);
                }}
                isRequiredField
                placeholder={t('name')}
                labelText={t('name')}
                name="name"
              />
            </Grid>
            <Grid size={{
              xs: 12, sm: 6, md: 6, lg: 6,
            }}
            >
              <div className="align-center">
                <BaseLabel letterSpacing="-0.5px">{t('permissions')}</BaseLabel>
                <Icon color={BASE_COLORS.error} className="mb-1 ml-1" path={mdiAsterisk} size={0.5} />
              </div>
              <BaseCombobox
                multiple
                variant="outlined"
                value={formInputs.visibility as any}
                options={roleOptions as unknown as Array<TLocation>}
                onChange={(visibility) => handleFormChange('visibility', visibility)}
              />
            </Grid>
            <Grid size={{
              xs: 12, sm: 6, md: 6, lg: 6,
            }}
            >
              <div className="align-center">
                <BaseLabel letterSpacing="-0.5px">{t('locations')}</BaseLabel>
                <Icon color={BASE_COLORS.error} className="mb-1 ml-1" path={mdiAsterisk} size={0.5} />
              </div>
              <BaseCombobox<TLocation>
                multiple
                variant="outlined"
                value={formInputs.locations}
                options={locationsByPermissions}
                onChange={(locations) => handleFormChange('locations', locations)}
              />
            </Grid>
            <Grid size={{
              xs: 12, sm: 6, md: 6, lg: 6,
            }}
            >
              <div className="align-center">
                <BaseLabel letterSpacing="-0.5px">{t('assign', { ns: 'tasklists' })}</BaseLabel>
              </div>
              <AvatarSelect
                value={formInputs.assign}
                options={getMembersByPermission}
                onChange={(selectedMember) => handleFormChange('assign', selectedMember)}
              />
            </Grid>
            <Grid size={{
              xs: 12, sm: 12, md: 12, lg: 12,
            }}
            >
              <FormControlLabel
                control={(
                  <BaseCheckbox
                    handleClick={() => handleFormChange('notifyAssignee', !formInputs.notifyAssignee)}
                    value={formInputs.isAssigned}
                    disabled={!formInputs?.assign?.email}
                  />
                )}
                label={t('informAssignEmployee', { ns: 'tasklists' })}
              />
            </Grid>
            <Grid size={{
              xs: 12, sm: 4, md: 4, lg: 4,
            }}
            >
              <FormControlLabel
                control={(
                  <BaseCheckbox
                    checked={getFormInputs.isDated}
                    value={getFormInputs.isDated}
                    handleClick={() => handleFormChange('isDated', !getFormInputs.isDated)}
                  />
                )}
                label={t('datedTasklists', { ns: 'tasklists' })}
              />
            </Grid>
            <Grid size={{
              xs: 12, sm: 8, md: 8, lg: 8,
            }}
            >
              <InputDatePicker
                disabled={!getFormInputs.isDated}
                onChange={(val) => handleFormChange('date', moment.tz(val, getTimezone()))}
                placeholder={t('enterTasklistDate', { ns: 'tasklists' })}
                minDate={moment()}
                name="tasklistPicker"
              />
            </Grid>
            <Grid
              size={{
                xs: 12, sm: 12, md: 12, lg: 12,
              }}
            >
              <BaseLabel letterSpacing="-0.5px">{t('tasklistDescription', { ns: 'tasklists' })}</BaseLabel>
              <TextField
                value={formInputs.description}
                onChange={(event) => handleFormChange('description', event.target.value)}
                multiline
                fullWidth
                rows={5}
              />
            </Grid>
            <Grid size={{
              xs: 12, sm: 12, md: 12, lg: 12,
            }}
            >
              <CreateEditTasks
                rules={{
                  taskValidation: (task: string) => taskValidation(task, formInputs.tasks),
                }}
                onTaskDrag={(tasks) => handleFormChange('tasks', tasks)}
                deleteTask={deleteTask}
                addTask={addTask}
                tasks={formInputs.tasks}
              />
            </Grid>
            <Grid
              size={{
                xs: 12, lg: 12,
              }}
              py={4}
            >
              <LoadingButton
                onClick={methods.handleSubmit(addOrEditTasklist)}
                loading={isEditLoading || isAddLoading}
                disabled={!methods.formState.isValid || !isEdited || !formInputs.tasks?.length}
                size="large"
                variant="contained"
              >
                {dataByMode.mainBtnText}
              </LoadingButton>
            </Grid>
          </Grid>
        </Box>
      </FormProvider>
    </BaseDrawer>
  );
};

export {
  CreateEditTasklist,
};
